import React from 'react';
import { getAssetPath } from 'utils/asset-utils';

interface IProps {
  text: string;
  author?: string;
  avatarSrc?: string; // pass a public path; will be wrapped with getAssetPath if starting with /images
  variant?: 'subtle' | 'highlight';
  className?: string;
}

export const Quote: React.FC<IProps> = ({ text, author, avatarSrc, variant = 'subtle', className }) => {
  if (variant === 'highlight') {
    return (
      <div className={`rounded-2xl bg-strawberry-100 p-6 md:rounded-3xl md:p-10 ${className || ''}`}>
        <div className='mb-4 flex size-10 items-center justify-center rounded-full bg-strawberry-200 md:mb-6 md:size-12'>
          <i className='fal fa-quote-left text-lg text-strawberry-600 md:text-xl' />
        </div>
        <p className='text-xl font-bold leading-relaxed text-eggplant-600 md:text-3xl'>{text}</p>
        {author ? (
          <p className='mt-6 font-caffeine-scribble text-xl text-raspberry md:mt-8 md:text-2xl'>{author}</p>
        ) : null}
      </div>
    );
  }

  // subtle variant (eggplant card with corner quote mark and optional avatar)
  const resolvedAvatar = avatarSrc && avatarSrc.startsWith('/images') ? getAssetPath(avatarSrc) : avatarSrc;
  return (
    <div className={`relative rounded-lg bg-eggplant-50 p-4 md:p-8 ${className || ''}`}>
      <div className='absolute left-4 top-4 inline-flex size-6 rotate-180 flex-col items-center justify-start rounded-[50px] bg-[#FFBCBC] pb-1 pt-0.5 md:left-6 md:top-6 md:size-7 md:pt-[3px]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='14'
          height='18'
          viewBox='0 0 18 23'
          fill='none'
          className='md:h-[23px] md:w-[18px]'>
          <path
            d='M14.4037 3.90984C12.1651 3.90984 10.7544 5.02918 10.1717 7.26784C9.58905 9.41451 9.89572 11.7298 11.0917 14.2138C12.3184 16.7898 14.0357 18.3538 16.2437 18.9058C16.7344 19.0285 17.2404 19.0898 17.7617 19.0898V17.4798C16.0444 17.4798 14.6491 16.6518 13.5757 14.9958C12.7477 13.6772 12.5484 12.5578 12.9777 11.6378C13.2231 11.0858 13.6984 10.8098 14.4037 10.8098C15.3237 10.8098 16.1134 10.4725 16.7727 9.79784C17.4321 9.12318 17.7617 8.31051 17.7617 7.35984C17.7617 6.40918 17.4321 5.59651 16.7727 4.92184C16.1134 4.24718 15.3237 3.90984 14.4037 3.90984ZM4.74372 3.90984C2.50505 3.90984 1.09439 5.02918 0.511721 7.26784C-0.0709457 9.41451 0.23572 11.7298 1.43172 14.2138C2.65839 16.7898 4.37572 18.3538 6.58372 18.9058C7.07439 19.0285 7.58039 19.0898 8.10172 19.0898V17.4798C6.38439 17.4798 4.98905 16.6518 3.91572 14.9958C3.08772 13.6772 2.88839 12.5578 3.31772 11.6378C3.56305 11.0858 4.03839 10.8098 4.74372 10.8098C5.66372 10.8098 6.45339 10.4725 7.11272 9.79784C7.77205 9.12318 8.10172 8.31051 8.10172 7.35984C8.10172 6.40918 7.77205 5.59651 7.11272 4.92184C6.45339 4.24718 5.66372 3.90984 4.74372 3.90984Z'
            fill='#F01D59'
          />
        </svg>
      </div>
      <div className='pl-6 md:pl-8'>
        <blockquote className='mb-4 text-base italic leading-relaxed text-neutral-800 md:text-lg'>
          <div className='text-red-950 justify-center text-base font-medium leading-relaxed sm:text-lg md:text-xl'>
            {text}
          </div>
        </blockquote>
        {author || resolvedAvatar ? (
          <div className='flex items-center'>
            {resolvedAvatar ? (
              <img
                src={resolvedAvatar}
                alt={author || 'Quote author'}
                className='mr-3 size-8 shrink-0 rounded-full md:size-10'
              />
            ) : null}
            {author ? (
              <cite className='font-medium italic text-neutral-800'>
                <span className='text-rose-600 justify-start text-base leading-relaxed sm:text-lg md:text-xl'>
                  {author}
                </span>
              </cite>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
